import { useEffect, useState } from 'react';
import { Edit, Plus } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { VacationPolicy } from '../api/ferias';
import { PessoasModal } from '../components/PessoasModal';
import { useEmpresasAtivas } from '../hooks';
import { usePoliticasFerias, useSalvarPolitica } from '../hooks/ferias';

const NOVA: Partial<VacationPolicy> = {
  companyId: null, name: '', maxInstallments: 3, allowSell: true, maxSellDays: 10, allow13thAdvance: true,
  minNoticeDays: 30, managerApproval: true, hrApproval: true, alertDays: [90, 60, 30], active: true,
};

/** Políticas de férias (design D1): uma padrão e, opcionalmente, uma por empresa. */
export default function PoliticasFeriasPage() {
  const politicas = usePoliticasFerias();
  const [editing, setEditing] = useState<Partial<VacationPolicy> | null>(null);

  return (
    <PageContainer
      title="Políticas de férias"
      subtitle="Os mínimos legais (14 e 5 dias por parcela, 1/3 de venda) valem sempre; a política só restringe mais."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Políticas de férias' }]}
      action={
        <Can feature={FEATURES.pessoasFeriasPoliticas} action="create">
          <Button onClick={() => setEditing({ ...NOVA })}><Plus className="mr-2 h-5 w-5" /> Política por empresa</Button>
        </Can>
      }
    >
      <ul className="space-y-3">
        {(politicas.data ?? []).map((p) => (
          <li key={p.id} className={`rounded-lg border border-gray-200 bg-white p-4 text-sm ${p.active ? '' : 'opacity-60'}`}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-gray-900">
                {p.name} <span className="font-normal text-gray-500">· {p.companyName ?? 'Padrão (todas as empresas)'}</span>
                {!p.active && <span className="ml-2 text-xs text-gray-500">inativa</span>}
              </p>
              <Can feature={FEATURES.pessoasFeriasPoliticas} action="edit">
                <button type="button" className="rounded p-1.5 text-gray-500 hover:bg-gray-100" onClick={() => setEditing(p)} aria-label={`Editar ${p.name}`}>
                  <Edit className="h-4 w-4" />
                </button>
              </Can>
            </div>
            <p className="mt-1 text-gray-700">
              Até {p.maxInstallments} parcela(s) · {p.allowSell ? `venda até ${p.maxSellDays} dias` : 'sem venda'}
              {' · '}{p.allow13thAdvance ? 'adianta 13º' : 'sem adiantamento do 13º'} · antecedência de {p.minNoticeDays} dias
            </p>
            <p className="text-gray-700">
              Aprovação: {[p.managerApproval && 'gestor', p.hrApproval && 'RH'].filter(Boolean).join(' → ') || 'automática'}
              {' · '}alertas a {p.alertDays.join(', ')} dias do vencimento
            </p>
          </li>
        ))}
      </ul>
      <PoliticaModal value={editing} onClose={() => setEditing(null)} />
    </PageContainer>
  );
}

function PoliticaModal({ value, onClose }: { value: Partial<VacationPolicy> | null; onClose: () => void }) {
  const toast = useToast();
  const salvar = useSalvarPolitica();
  const empresas = useEmpresasAtivas();
  const [form, setForm] = useState<Partial<VacationPolicy>>(NOVA);
  const [alertas, setAlertas] = useState('90, 60, 30');
  const [error, setError] = useState<string | null>(null);
  const editando = !!value?.id;
  const padrao = editando && value?.companyId === null;

  useEffect(() => {
    if (!value) return;
    setForm(value);
    setAlertas((value.alertDays ?? [90, 60, 30]).join(', '));
    setError(null);
  }, [value]);

  const set = <K extends keyof VacationPolicy>(k: K, v: VacationPolicy[K]) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    const alertDays = alertas.split(/[,;\s]+/).filter(Boolean).map(Number);
    if (alertDays.length === 0 || alertDays.some((n) => !Number.isInteger(n) || n < 1 || n > 365)) {
      return setError('Informe os marcos de alerta em dias (1 a 365), separados por vírgula.');
    }
    if (!form.name?.trim()) return setError('Informe o nome.');
    if (!editando && !form.companyId) return setError('Escolha a empresa.');
    const payload: Partial<VacationPolicy> = {
      name: form.name.trim(), maxInstallments: form.maxInstallments, allowSell: form.allowSell, maxSellDays: form.maxSellDays,
      allow13thAdvance: form.allow13thAdvance, minNoticeDays: form.minNoticeDays, managerApproval: form.managerApproval,
      hrApproval: form.hrApproval, alertDays: [...new Set(alertDays)],
      ...(editando ? { active: form.active } : { companyId: form.companyId }),
    };
    try {
      await salvar.mutateAsync({ id: value?.id ?? null, payload });
      toast.success(editando ? 'Política atualizada.' : 'Política criada.');
      onClose();
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível salvar a política.'));
    }
  }

  const check = (k: 'allowSell' | 'allow13thAdvance' | 'managerApproval' | 'hrApproval' | 'active', label: string, disabled = false) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={!!form[k]} disabled={disabled} onChange={(e) => set(k, e.target.checked)} /> {label}
    </label>
  );

  return (
    <PessoasModal open={value !== null} size="lg" title={editando ? `Editar — ${value?.name}` : 'Nova política por empresa'}
      submitLabel="Salvar" isSubmitting={salvar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {!editando && (
          <AutocompleteSelect label="Empresa" options={(empresas.data ?? []).map((c) => ({ value: c.id, label: c.tradeName }))}
            value={form.companyId ?? ''} onChange={(v) => set('companyId', v)} />
        )}
        <TextInput label="Nome" value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />
        <AutocompleteSelect label="Parcelas" options={[1, 2, 3].map((n) => ({ value: String(n), label: `Até ${n}` }))}
          value={String(form.maxInstallments ?? 3)} onChange={(v) => set('maxInstallments', Number(v))} />
        <TextInput label="Antecedência mínima (dias)" type="number" min={0} max={365} value={String(form.minNoticeDays ?? 30)}
          onChange={(e) => set('minNoticeDays', Number(e.target.value) || 0)} />
        <TextInput label="Venda máxima (dias, até 10)" type="number" min={0} max={10} value={String(form.maxSellDays ?? 0)}
          disabled={!form.allowSell} onChange={(e) => set('maxSellDays', Math.min(10, Number(e.target.value) || 0))} />
        <TextInput label="Alertas antes do vencimento (dias)" value={alertas} onChange={(e) => setAlertas(e.target.value)} />
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        {check('allowSell', 'Permite vender férias (abono)')}
        {check('allow13thAdvance', 'Permite adiantar o 13º')}
        {check('managerApproval', 'Aprovação do gestor imediato')}
        {check('hrApproval', 'Aprovação do RH')}
        {editando && check('active', 'Ativa', padrao)}
      </div>
      {!form.managerApproval && !form.hrApproval && (
        <p className="text-xs text-amber-700">Sem etapas de aprovação, as solicitações válidas nascem aprovadas.</p>
      )}
    </PessoasModal>
  );
}
